<template>
  <section class="form-limited-width">
    <base-form-group
      v-if="!isCourseInThePast && daysToCourseEndDate"
      :label="$t('src/components/training/traininggenerator.2951')"
    >
      <h6>{{ daysToCourseEndDate }} {{ declensionDaysToCourseEnd }}</h6>
    </base-form-group>
    <base-form-group
      :label="$t('src/components/training/traininggenerator.6743')"
      :is-invalid="$v.frequency.$error"
      :errors="{
        required: !$v.frequency.required
      }"
    >
      <base-dropdown-select
        :options="frequencyList"
        v-model="frequency"
        class="form-control"
      ></base-dropdown-select>
    </base-form-group>
    <base-form-group
      :label="$t('src/components/training/traininggenerator.2453')"
      :is-invalid="$v.count.$error"
      :errors="{
        required: !$v.count.required,
        numeric: !$v.count.numeric,
        minValue: !$v.count.minValue
      }"
      :messages="{
        minValue: `${$t('src/components/training/traininggenerator.minNumberSessions')}`
      }"
    >
      <base-input
        v-model.lazy.number="count"
        name="count"
        @blur="$v.count.$touch()"
        :is-invalid="$v.count.$error"
      >
      </base-input>
    </base-form-group>
    <base-form-group
      :label="$t('src/components/training/traininggenerator.3676')"
      :is-invalid="$v.description.$error"
      :errors="{
        allowSymbols: !$v.description.allowSymbols
      }"
    >
      <base-textarea v-model="description" name="description" @blur="$v.description.$touch()">
      </base-textarea>
    </base-form-group>
    <div class="mt-3">
      <h6>{{ $t('src/components/training/traininggenerator.6301') }}</h6>
      <base-form-group :label="$t('src/components/training/traininggenerator.7383')" class="ml-2">
        <h6 v-if="isCourseWatchDuration" style="padding-top: 10px">
          <duration-with-unit
            v-model="courseDuration"
            :is-readonly="true"
            :durationUnit="courseUnit"
          ></duration-with-unit>
        </h6>
        <h6 v-else style="padding-top: 10px">{{ $t('src/components/training/traininggenerator.4216') }}</h6>
      </base-form-group>

      <base-form-group :label="$t('general.limits')" class="ml-2">
        <h6 v-if="isCourseWatchHr" style="padding-top: 10px">{{ heartRateLimitStr }}</h6>
        <h6 v-else style="padding-top: 10px">{{ $t('src/components/training/traininggenerator.4216') }}</h6>
      </base-form-group>
    </div>
    <div class="mt-4">
      <button class="btn btn-primary" type="button" @click="generateTrainings">
        {{ $t('button.generate') }}
      </button>
    </div>
  </section>
</template>

<script>
import { status, DATE_FORMAT, HEART_RATE, TRAINING_DEFAULT_COMMENTS } from '@/main.config.js'
import declension from '@/modules/declension.js'
import { required, numeric, minValue } from 'vuelidate/lib/validators'
import { allowSymbols } from '@/modules/validators'
import { nowMoment } from '@/mixins/now-moment.js'
import { courseDate } from '@/mixins/course-date'
import { trainingDate } from '@/mixins/training-date.js'

export default {
  name: 'TrainingGenerator',
  mixins: [nowMoment, courseDate, trainingDate],
  components: {
    BaseFormGroup: () => import('@/base/BaseFormGroup.vue'),
    BaseInput: () => import('@/base/BaseInput.vue'),
    BaseTextarea: () => import('@/base/BaseTextarea.vue'),
    BaseDropdownSelect: () => import('@/base/BaseDropdownSelect.vue'),
    DurationWithUnit: () => import('@/components/DurationWithUnit.vue')
  },
  props: {
    course: {
      type: Object
    },
    lastTrainingDate: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      count: 12,
      frequency: '3',
      frequencyList: {
        1: this.$t('frequency.once'),
        2: this.$t('frequency.every2'),
        3: this.$t('frequency.inOneDay'),
        7: this.$t('frequency.everyDay')
      },
      description: TRAINING_DEFAULT_COMMENTS.base,
      trainings: [],
      minRate: HEART_RATE.min,
      maxRate: HEART_RATE.max
    }
  },
  validations() {
    return {
      frequency: {
        required
      },
      count: {
        required,
        numeric,
        minValue: minValue(1)
      },
      description: {
        allowSymbols
      }
    }
  },

  computed: {
    courseDuration() {
      return this.course.training_duration
    },
    courseUnit() {
      return this.course.training_duration_unit
    },
    durationInMinutes() {
      if (this.course.opt_show_duration) {
        return this.getDurationInMinutes(this.course.training_duration, this.course.training_duration_unit)
      } else {
        return null
      }
    },
    declensionDuration() {
      return declension(this.duration, [
        `${this.$tc('unit.minutes', 0)}`,
        `${this.$tc('unit.minutes', 1)}`,
        `${this.$tc('unit.minutes', 2)}`
      ])
    },
    firstTrainingDate() {
      //все даты в utc
      if (!this.lastTrainingDate) {
        return this.nowMoment.utc()
      }
      let lastTrainingDate = this.lastTrainingDate.clone()
      const firstTrainingDate = lastTrainingDate.add(this.daysAddCount, 'd')
      if (firstTrainingDate.isBefore(this.nowMoment)) {
        return this.nowMoment.utc()
      } else {
        return firstTrainingDate.utc()
      }
    },
    daysAddCount() {
      return Math.floor(7 / parseInt(this.frequency))
    },
    daysToCourseEndDate() {
      if (!this.courseMomentEndDate) {
        return null
      }
      return this.courseMomentEndDate.diff(this.nowMoment, 'days')
    },
    declensionDaysToCourseEnd() {
      return declension(this.daysToCourseEndDate, [
        `${this.$tc('unit.day', 0)}`,
        `${this.$tc('unit.day', 1)}`,
        `${this.$tc('unit.day', 2)}`
      ])
    },
    isCourseInThePast() {
      return this.daysToCourseEndDate < 0
    },
    isCourseWatchHr() {
      return !!this.course.opt_watch_hr
    },
    isCourseWatchDuration() {
      return !!this.course.opt_show_duration
    },
    heartRateLimitStr() {
      return `${this.course.course_hr_min} — ${this.course.course_hr_max}`
    }
  },
  watch: {
    daysAddCount() {
      this.setCountByCourseCloseDate()
    }
  },
  mounted() {
    this.setCountByCourseCloseDate()
  },
  methods: {
    generateTrainings() {
      if (!this.$v.$invalid) {
        let date = this.firstTrainingDate.clone()
        let isUpdateCourseCloseDate = false

        for (let i = 0; i < this.count; i++) {
          this.trainings.push({
            course_id: this.course.id,
            status: status.plan,
            date_plan: date.format(DATE_FORMAT.server),
            date_start: date.format(DATE_FORMAT.server),
            heart_rate_min: this.course.opt_watch_hr ? this.course.course_hr_min : null,
            heart_rate_max: this.course.opt_watch_hr ? this.course.course_hr_max : null,
            duration: this.durationInMinutes,
            description: this.description
          })

          date.add(this.daysAddCount, 'd')
          if (this.courseMomentEndDate && date.isAfter(this.courseMomentEndDate)) {
            isUpdateCourseCloseDate = true
          }
        }
        this.$emit('generate', this.trainings, isUpdateCourseCloseDate)
      }
    },
    setCountByCourseCloseDate() {
      if (!this.daysToCourseEndDate || this.isCourseInThePast) {
        return
      }
      this.count = Math.round((this.daysToCourseEndDate + 1) / this.daysAddCount)
    }
  }
}
</script>
